import React, { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Global Turbo type declaration
declare global {
  const Turbo: {
    visit: (url: string, options?: { frame?: string }) => void;
  };
}

// Components
import LoadingBackdrop from "../components/LoadingBackdrop";
import FlashcardSetupModal from "../components/FlashcardSetupModal/FlashcardSetupModal";
import AvailableSoonModal from "../components/AvailableSoonModal";

// Generate Components
import GenerateModal from "../Generate/Modal";
import QuizSetupModal from "../components/QuizSetupModal";
import styles from "./style.module.scss";

// Hooks
import { useMobile } from "../utils/hooks/isMobile";

// Utils
import * as urls from "../utils/urls";
import ThemeProvider from "../utils/theme";
import useIsDarkMode from "../utils/hooks/darkMode";
import useCsrfToken from "../utils/hooks/csfrToken";

// Types
import { QuizSetupData } from "../types";
import globalModalManager from "../utils/globalModalManager";

type StudyToolsProps = {
  dashboard?: boolean;
  drawer?: boolean;
  mobile_dashboard?: boolean;
  context: 'dashboard' | 'drawer' | 'mobile_dashboard';
  folder_id?: string | number;
  languages_select_options?: Record<string, string>;
}

const StudyTools: React.FC<StudyToolsProps> = ({ dashboard, drawer, mobile_dashboard, context, folder_id, languages_select_options }) => {
  // Hooks
  const { t } = useTranslation();
  const csrfToken = useCsrfToken();
  const isMobile = useMobile();

  useEffect(() => {
    const handleQuizTrigger = () => { setQuizSetupModal(true) };
    const handleFlashcardTrigger = () => { setShowFlashcardSetupModal(true) };
    const handleVideoTrigger = () => { setAvailableSoonModal(true) };
    // Locked for now
    const handlePodcastTrigger = () => { globalModalManager.openModal('available-soon') };

    window.addEventListener('openQuizSetupModal', handleQuizTrigger);
    window.addEventListener('openFlashcardSetupModal', handleFlashcardTrigger);
    window.addEventListener('openVideoModal', handleVideoTrigger);
    window.addEventListener('openPodcastModal', handlePodcastTrigger);
    return () => {
      window.removeEventListener('openQuizSetupModal', handleQuizTrigger);
      window.removeEventListener('openFlashcardSetupModal', handleFlashcardTrigger);
      window.removeEventListener('openVideoModal', handleVideoTrigger);
      window.removeEventListener('openPodcastModal', handlePodcastTrigger);
    };
  }, []);

  // States
  const [generateModal, setGenerateModal] = useState<'video' | 'podcast' | null>(null);
  const [quizSetupModal, setQuizSetupModal] = useState<boolean>(false);
  const [availableSoonModal, setAvailableSoonModal] = useState<boolean>(false);
  const [showFlashcardSetupModal, setShowFlashcardSetupModal] = useState<boolean>(false);

  const [processing, setProcessing] = useState<boolean>(false);

  const handleGenerateQuiz = useCallback(async (data: QuizSetupData) => {
    setQuizSetupModal(false);
    setProcessing(true);

    try {
      const { note, files, links, prompt, timeLimit, questions, adaptiveMix, skippingHints, max_questions, language } = data;
      const hasFiles = files && files.length > 0;
      const hasLinks = links && links.length > 0;
      const hasPrompt = prompt && prompt.trim().length > 0;
      const hasMaterials = note || hasFiles || hasLinks;
      const isPromptBased = !hasMaterials && hasPrompt;

      if (!hasMaterials && !isPromptBased) {
        console.error('No sources provided for quiz generation');
        alert(t('studyTools.alerts.missing_source'));
        return;
      }

      const formData = new FormData();

      if (isPromptBased) {
        formData.append('prompt', prompt);
      }
      else {
        if (note) {
          formData.append('quiz[selected_smart_notes][]', note.id.toString());
        }

        if (hasFiles) {
          files!.forEach((fileId) => {
            formData.append('quiz[files][]', fileId);
          });
        }

        if (hasLinks) {
          links!.forEach((link) => {
            formData.append('quiz[links][]', link);
          });
        }
      }

      if (folder_id) {
        formData.append('folder_id', folder_id.toString());
      }

      formData.append('quiz[language]', language || 'en');
      formData.append('quiz[max_questions]', max_questions.toString());
      formData.append('quiz[adaptive_mix]', adaptiveMix.toString());
      formData.append('quiz[skipping_hints]', skippingHints.toString());

      if (timeLimit) {
        formData.append('quiz[time_limit]', timeLimit.toString());
      }

      questions.forEach((question, index) => {
        formData.append(`quiz[questions][${index}][question_type]`, question.question_type);
        formData.append(`quiz[questions][${index}][qty]`, question.qty.toString());
      });

      const url = isPromptBased ? urls.aiPromptQuizzesUrl() : urls.aiQuizzesUrl();
      const options = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      };

      const response = await fetch(url, options);

      if (response.ok) {
        const result = await response.json();
        if (result.data?.id) {
          const url = folder_id
            ? `${urls.quizUrl(result.data.id)}?folder_id=${folder_id}`
            : urls.quizUrl(result.data.id);
          Turbo.visit(url);
        } else {
          console.error('No quiz ID returned from server');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Quiz generation failed:', response.status, errorData);
        alert(t('studyTools.alerts.generation_failed'));
      }
    } catch (error) {
      console.error('Error generating quiz:', error);
      alert(t('studyTools.alerts.unexpected_error'));
    } finally {
      setProcessing(false);
    }
  }, [csrfToken, t]);

  let rootStyle = `${styles.root}`
  if (drawer) rootStyle += ` ${styles.drawer}`
  if (mobile_dashboard) rootStyle += ` ${styles.mobileDashboard}`
  if (context === 'mobile_dashboard') rootStyle += ` ${styles['root_scrollable']}`

  return (
    <ThemeProvider>
      <div className={rootStyle}>
        {processing && <LoadingBackdrop />}

        <GenerateModal open={!!generateModal} onClose={() => setGenerateModal(null)} type={generateModal} />

        <QuizSetupModal allowFileUpload open={!!quizSetupModal} onClose={() => setQuizSetupModal(false)} onGenerate={handleGenerateQuiz} languages={languages_select_options} />

        <AvailableSoonModal open={availableSoonModal} onClose={() => setAvailableSoonModal(false)} />

        <FlashcardSetupModal
          open={showFlashcardSetupModal}
          onClose={() => setShowFlashcardSetupModal(false)}
          studyTools
          dashboard={dashboard}
          drawer={drawer}
          mobile_dashboard={mobile_dashboard}
          folder_id={folder_id}
          languages={languages_select_options}
        />
      </div>
    </ThemeProvider>
  )
};

export default StudyTools;
